import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, SearchableDropdown, SideBarModal } from '@/components';
import { RepossessionSeverity } from '@/types/common';
import { RentalDetail } from '@/types/rental';
import { createRepossession } from '@/services/api/repossessions';
import { getListUser, User } from '@/services/api/users';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { getUserEmail } from '@/utils/storage';
import { extractApiErrorMessage } from '@/utils';

interface RequestRepossessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Navigate to the created case; the parent owns routing. */
  onCreated: (repoId: string) => void;
  detail: RentalDetail;
}

const SEVERITY_OPTIONS = Object.values(RepossessionSeverity).map((value) => ({
  value,
  label: value,
}));

const inputClass = (hasError?: boolean) =>
  `w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none focus:ring-1 focus:ring-primary ${
    hasError ? 'border-red-500' : 'border-gray-200'
  }`;

/**
 * The bridge from a rental's escalation to a repossession case. The case is
 * keyed by the rental's handover (the repossession module knows units, not
 * contracts), so this button only renders while that handover is ACTIVE —
 * the same gate POST /v1/repossessions enforces.
 */
const RequestRepossessionModal: React.FC<RequestRepossessionModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  detail,
}) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [severity, setSeverity] = useState<RepossessionSeverity>(
    RepossessionSeverity.HIGH
  );
  const [assignedOpsTeam, setAssignedOpsTeam] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonError, setReasonError] = useState('');

  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      // Same query RepoModal uses — the endpoint pages at 10 by default,
      // which reads as an empty dropdown.
      const response = await getListUser({ page: '1', size: '999' });
      setUsers(response.data || []);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal memuat daftar user')
      );
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setNotes('');
    setSeverity(RepossessionSeverity.HIGH);
    setAssignedOpsTeam('');
    setReasonError('');
    loadUsers();
  }, [isOpen, loadUsers]);

  // Email as the value — the same identity RepoModal stores in
  // assignedOpsTeam, so cases created here match the repossession screens.
  const userOptions = useMemo(
    () =>
      users
        .filter((user) => user.email)
        .map((user) => ({ value: user.email, label: user.email })),
    [users]
  );

  const handoverID = detail.handover?.ID;

  const handleSubmit = async () => {
    // Mirrors the backend DTO: reason 15–200 chars.
    if (reason.trim().length < 15) {
      setReasonError('Alasan minimal 15 karakter');
      return;
    }
    if (!handoverID || !assignedOpsTeam) return;
    setIsSubmitting(true);
    try {
      const created = await createRepossession({
        handoverID,
        reason: reason.trim(),
        notes: notes.trim(),
        assignedOpsTeam,
        severity,
        createdBy: getUserEmail() || 'FMS_WEB',
      });
      showSuccessToast('Success', 'Case repossession dibuat');
      onCreated(created?.ID ?? '');
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal membuat case repossession')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="md"
      header={
        <h2 className="text-[18px] font-semibold text-gray-900">
          Request Repossession
        </h2>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="medium" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            size="medium"
            disabled={
              isSubmitting ||
              !handoverID ||
              !assignedOpsTeam ||
              reason.trim().length === 0
            }
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Memproses...' : 'Buat Case'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-700">
          <span className="font-medium">
            {detail.vehicle?.platNomor ?? '-'}
          </span>
          {detail.driver?.name ? ` • ${detail.driver.name}` : ''} — case akan
          terhubung ke handover rental ini.
        </div>

        <div>
          <p className="text-[13px] text-gray-500 mb-1">
            Alasan <span className="text-red-500">*</span>
          </p>
          <textarea
            className={inputClass(Boolean(reasonError))}
            rows={3}
            maxLength={200}
            value={reason}
            placeholder="Minimal 15 karakter — contoh: mitra tidak dapat dihubungi 4 hari, unit terpantau berpindah kota"
            onChange={(event) => {
              setReason(event.target.value);
              if (reasonError) setReasonError('');
            }}
          />
          {reasonError && (
            <p className="mt-1 text-[12px] text-red-500">{reasonError}</p>
          )}
        </div>

        <div>
          <p className="text-[13px] text-gray-500 mb-1">
            Severity <span className="text-red-500">*</span>
          </p>
          <SearchableDropdown
            options={SEVERITY_OPTIONS}
            value={severity}
            onChange={(value: string) =>
              setSeverity(value as RepossessionSeverity)
            }
            placeholder="Pilih severity"
          />
        </div>

        <div>
          <p className="text-[13px] text-gray-500 mb-1">
            Assigned Ops Team <span className="text-red-500">*</span>
          </p>
          <SearchableDropdown
            options={userOptions}
            value={assignedOpsTeam}
            onChange={(value: string) => setAssignedOpsTeam(value)}
            placeholder={isLoadingUsers ? 'Memuat user...' : 'Pilih anggota tim'}
            loading={isLoadingUsers}
          />
        </div>

        <div>
          <p className="text-[13px] text-gray-500 mb-1">Notes</p>
          <textarea
            className={inputClass()}
            rows={2}
            maxLength={200}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </div>
    </SideBarModal>
  );
};

export default RequestRepossessionModal;
